<template>
  <div class="text-item" :class="isStyledItem ? 'text-center' : ''">
    <div v-if="icon" class="text-item__icon mx-auto mb-5"  :class="[
      isIconReplaced ? 'logo_as_icon' : '',
      colorVariant ? 'shaded-fill-' + colorVariant : ''
    ]">
      <logo v-if="this.isIconReplaced" class="mt-2" :whitelabel-config="whitelabelConfig" />
      <simple-svg v-else :src="`${ASSETS_URL}${icon}`" />
    </div>
    <span
      v-html="
        $t(itemText, {
          ASSETS_URL,
          ...additionalData,
          parent,
          currency: locale.currency,
          token: interactionInstanceToken,
          locale: locale.id,
          externalUserToken: externalUserReference,
          evaluatorDashboardLink: evaluatorDashboardLink,
          clientInteractionResultsLink: clientInteractionResultsLink,
        })
      "
    />
  </div>
</template>

<script>
import { formatNumber } from "@/utils/numbers";
import { ASSETS_URL } from "@/constants/misc";
import { ICON, STYLE_ITEM, LOGO_IN_ICON } from "@/constants/items";
import question from "@/components/ui/question";
import { htmlEscape, isDone } from "@/utils/items";
import { QUERY_PARAM_IFRAME } from '@/constants/misc'
import { mapGetters } from 'vuex'
import logo from '@/components/ui/logo'
import { DEFAULT_LOCALE } from '@/constants/i18n'

export default {
  name: "textItem",
  components: {
    question,
    logo,
  },
  props: {
    item: {
      type: Object,
    },
    itemProperties: {
      type: Object,
    },
    metaData: {
      type: Object,
    },
    interactionInstanceToken: {
      type: String,
    },
    locale: {
      type: Object,
    },
    externalUserReference: {
      type: String,
    },
    organizationType: {
      type: String,
    },
    productLevel: {
      type: String,
    },
    productType: {
      type: String,
    },
    clientToken: {
      type: String,
    },
    colorVariant: {
      type: String,
    },
  },
  computed: {
    ...mapGetters([
      'whitelabelConfig',
    ]),
    productLevelSane() {
      return this.productLevel ? `_${this.productLevel}` : "_DEFAULT";
    },
    itemText() {
      const iframeTranslation = `${this.item.text}_IFRAME`;
      const fullTranslation=`${this.item.text}_${this.productType}_${this.organizationType}${this.productLevelSane}`;
      const fallbackTranslation=`${this.item.text}`;

      const fullTranslationExists = this.$te(fullTranslation, 'EN_US');
      const fullTranslationExistsInFallback = this.$te(fullTranslation, DEFAULT_LOCALE.language);
      const isIFrame = this.$route.query.embed === QUERY_PARAM_IFRAME;

      if(isDone(this.item) && isIFrame) {
        return iframeTranslation;
      } else if (fullTranslationExists || fullTranslationExistsInFallback) {
        return fullTranslation;
      } else {
        return fallbackTranslation;
      }
    },
    additionalData() {
      let metaData = this.metaData && this.metaData.metaInformation;
      // check if we have numerical INPUT_RESULT, if so parse to integer to display amount
      let amount = metaData && metaData["INPUT_RESULT"];
      if (amount && parseInt(parseFloat(amount).toFixed())) {
        //take care of decimal/exponential represetation with parseFlot/toFixed
        amount = formatNumber(parseInt(parseFloat(amount).toFixed()), this.locale.tag);
      }
      // escape all fields
      if (metaData) {
        let newMetaData = {};
        for (let fieldName in metaData) {
          newMetaData[fieldName] = htmlEscape(metaData[fieldName]);
        }
        metaData = newMetaData;
      }
      return {
        ...metaData,
        INPUT_RESULT: amount,
        externalUserReference: this.externalUserReference,
      };
    },
    icon() {
      return this.itemProperties.getProperty(ICON);
    },
    isStyledItem() {
      return this.itemProperties.getProperty(STYLE_ITEM);
    },
    isIconReplaced() {
      return this.itemProperties.getProperty(LOGO_IN_ICON);
    },
    parent() {
      // get main interactionToken from URL as it could be a parent interaction if subinteractions exist (BQS)
      // where instanceToken/interactionInstanceToken will be set to subInteraction, including the
      // selected language for the interaction
      if (this.$route.query.locale) {
        return `${this.$route.params.interactionInstanceToken}?locale=${this.$route.query.locale}`;
      }

      return this.$route.params.interactionInstanceToken;
    },
    clientInteractionResultsLink() {
      return `/i/${this.interactionInstanceToken}/results`;
    },
    evaluatorDashboardLink() {
      return `/login?redirectUrl=/product/${this.productType.toLowerCase()}/client/${
        this.clientToken
      }`;
    },
  },
  data() {
    return {
      ASSETS_URL,
    };
  },
  methods: {
    //Make sure BLABLA_secondary is rendered before BLABLA_primary
    waitForSvgAndFix() {
      const container = this.$el.querySelector(".text-item__icon");
      if (!container) return;

      const observer = new MutationObserver(() => {
        const svg = container.querySelector("svg");
        if (!svg) return;

        const groups = svg.querySelectorAll("g");
        if (!groups.length) return;

        let primary = null;
        let secondary = null;

        groups.forEach(g => {
          const id = g.getAttribute("id") || "";

          if (id.includes("_primary")) primary = g;
          if (id.includes("_secondary")) secondary = g;
        });

        if (primary && secondary) {
          svg.insertBefore(secondary, primary);
        }

        observer.disconnect();
      });

      observer.observe(container, {
        childList: true,
        subtree: true,
      });
    },
  },
  mounted() {
    // text item immediately emits result
    this.$emit("answer", { result: 1 });

    //Solve weird SVG-bug where layers are not in correct order (only seem to happen to "Decisions")
    this.waitForSvgAndFix();
  },
};
</script>
<style lang="scss">
.text-item {
  height: 100%;
}

.text-item__icon {
  width: 200px;

  &.logo_as_icon {
    width: 100%;
    
    .logo-container {
      margin: auto;

      img {
        width: 100%;
      }
    }
  }
}

.list-md-center {
  @media screen and (min-width: 768px) {
    text-align: left;
    width: 60%;
    margin: 0 auto;
  }

  li {
    margin-bottom: 0.3em;
  }
}

/**
    specific classes for styling OT Intro Text Item
  */
.ot-intro__list {
  li {
    position: relative;
    left: -1em;
  }
}

.ot-intro__options {
  display: flex;
  margin: 1em 0;
  min-height: 200px;
}

.ot-intro__option {
  height: 100%;
}

.ot-intro__icon {
  margin-top: 3px;
}

.ot-intro__option-header {
  height: 85px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0.3em;
  width: 100%;
  color: white;
  font-weight: 600;
  font-size: 1.25em;
  border-radius: 15px 15px 0 0;
  align-items: center;
  margin-bottom: 1em;
}

.ot-intro__option-text {
  font-size: 0.875em;
}

.ot-intro__securitas-header {
  background-color: $bq-turquoise;
}

.ot-intro__varietas-header {
  background-color: $bq-burgundy;
}

.ot-intro__option {
  background-color: $base_grey-4;
  border-radius: 15px;
}

.shaded-fill-bq-burgundy {
  g[id$='_primary'],
  svg[id$='_primary'] {
    fill: $bq-burgundy;
    * {
      fill: $bq-burgundy;
    }
  }

  g[id$='_secondary'],
  svg[id$='_secondary'] {
    fill: $bq-burgundy-light;
    * {
      fill: $bq-burgundy-light;
    }
  }
}

.shaded-fill-bq-turquoise {
  g[id$='_primary'],
  svg[id$='_primary'] {
    fill: $bq-turquoise;
    * {
      fill: $bq-turquoise;
    }
  }

  g[id$='_secondary'],
  svg[id$='_secondary'] {
    fill: $bq-turquoise-light;
    * {
      fill: $bq-turquoise-light;
    }
  }

  & .cls-3 {
    fill: none !important;
  }
}


.shaded-fill-bq-blue {
  g[id$='_primary'],
  svg[id$='_primary'] {
    fill: $bq-blue;
    * {
      fill: $bq-blue;
    }
  }

  g[id$='_secondary'],
  svg[id$='_secondary'] {
    fill: $bq-blue-light;
    * {
      fill: $bq-blue-light;
    }
  }

  & .cls-3 {
    fill: none !important;
  }
}

.shaded-fill-bq-red {
  g[id$='_primary'],
  svg[id$='_primary'] {
    fill: $bq-red;
    * {
      fill: $bq-red;
    }
  }

  g[id$='_secondary'],
  svg[id$='_secondary'] {
    fill: $bq-red-light;
    * {
      fill: $bq-red-light;
    }
  }

  & .cls-3 {
    fill: none !important;
  }
}

.shaded-fill-bq-orange {
  g[id$='_primary'],
  svg[id$='_primary'] {
    fill: $bq-orange;
    * {
      fill: $bq-orange;
    }
  }

  g[id$='_secondary'],
  svg[id$='_secondary'] {
    fill: $bq-orange-light;
    * {
      fill: $bq-orange-light;
    }
  }

  & .cls-3 {
    fill: none !important;
  }
}
</style>
